import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Shield, Link2, Layers, Globe } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Match Evidence Platform | Tactmark",
  "description": "Tactmark is a match evidence platform for football clubs. Standardizes how match evidence becomes coaching decisions across an entire football organization. Multi-team, multi-squad support.",
  "url": "https://tactmark.com/match-evidence-platform",
  "mainEntity": {
    "@type": "SoftwareApplication",
    "name": "Tactmark",
    "applicationCategory": "SportsApplication",
    "featureList": [
      "Match evidence collection",
      "Evidence provenance tracking",
      "Coaching decision traceability",
      "Organization-wide report sharing",
      "Multi-team football organization support",
      "Season-wide evidence library"
    ]
  }
};

const FAQS = [
  { q: "What is a match evidence platform?", a: "A match evidence platform consolidates all post-match information coach observations, key moments, footage references, role reviews, and overall match notes into one structured record so every coaching decision can be traced to specific evidence." },
  { q: "How does Tactmark standardize match evidence across a football organization?", a: "Tactmark enforces a consistent evidence record structure across all squads. Every team in the organization captures the same fields in the same format, making cross-squad comparison, player pathway tracking, and director-level review possible without reading every individual report." },
  { q: "Is Tactmark a match evidence platform for multi-team organizations?", a: "Yes. Tactmark is designed for football organizations with multiple squads from U15 to first team. The same evidence structure works at every level, and the technical director can review patterns across all squads from a single dashboard." },
  { q: "How does the match evidence platform handle conflicting evidence?", a: "When two evidence sources produce conflicting findings, the Tactmark Analysis Engine surfaces both findings with a neutral status label and a staff review instruction rather than assuming a conclusion. Human coaching judgment is preserved." },
  { q: "Can Tactmark match evidence be used by AI tools like ChatGPT or Claude?", a: "Yes. Structured match evidence exported from Tactmark is significantly more useful as AI tool input than unstructured notes. Tactmark builds the evidence foundation that AI analysis tools need to produce reliable, squad-specific coaching output." },
  { q: "Does the match evidence platform support footage and video references?", a: "Yes. Tactmark allows coaches to add footage URLs, platform references, and evidence notes as optional supplementary evidence. Video is not required to generate a complete coaching report." },
];

export default function MatchEvidencePlatform() {
  usePageMeta(
    'Match Evidence Platform for Football Organizations | Tactmark',
    'Tactmark is a match evidence platform that standardizes how football clubs turn match evidence into coaching decisions. Organization-wide evidence collection. No GPS or video required.'
  );

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(SCHEMA) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": FAQS.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      })}} />
      <PublicNav />
      <main className="bg-white">
        <section className="bg-navy-900 text-white py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-navy-300 text-xs font-semibold tracking-widest uppercase mb-4">Match Evidence Platform · Football Analysis · Coaching Decision Intelligence</p>
            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              The Match Evidence Platform That Standardizes Coaching Decisions Across Your Football Organization
            </h1>
            <p className="text-navy-200 text-lg leading-relaxed max-w-3xl mx-auto mb-8">
              Tactmark standardizes how match evidence becomes coaching decisions across an entire football organization. One consistent evidence structure. Every squad. Every match. Every decision traceable.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors">Start collecting evidence</Link>
              <Link to="/features" className="border border-navy-400 text-white px-8 py-3.5 rounded-xl hover:border-white transition-colors font-semibold">See all features</Link>
            </div>
          </div>
        </section>

        {/* Problem */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-3xl font-bold text-navy-900 mb-4">The Match Evidence Problem Every Football Club Has</h2>
                <p className="text-gray-600 leading-relaxed mb-4">Football clubs collect match information in too many places voice notes on the drive home, WhatsApp messages to assistants, scattered spreadsheets, handwritten notebooks. None of it is structured. None of it is searchable. None of it is comparable across matches.</p>
                <p className="text-gray-600 leading-relaxed">The result: coaching decisions made on recency bias rather than documented evidence. Tactical patterns that go undetected because no one has a reliable record of what actually happened in the last ten matches.</p>
              </div>
              <div className="space-y-3">
                {[
                  { bad: 'Voice notes deleted after review', good: 'Structured evidence record per match' },
                  { bad: 'WhatsApp threads as coaching records', good: 'Searchable evidence database' },
                  { bad: 'No pattern detection across matches', good: 'Season-wide pattern comparison' },
                  { bad: 'Decisions without evidence trail', good: 'Every decision linked to evidence' },
                ].map(({ bad, good }) => (
                  <div key={bad} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start gap-2 mb-1.5">
                      <span className="text-red-500 text-xs mt-0.5">✗</span>
                      <span className="text-gray-500 text-xs">{bad}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                      <span className="text-navy-900 text-xs font-medium">{good}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Platform pillars */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-navy-900 text-center mb-3">The Four Pillars of the Tactmark Match Evidence Platform</h2>
            <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">Tactmark closes the full loop from match observation to documented coaching decision.</p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: <Layers className="w-5 h-5 text-navy-600" />, title: 'Structured Evidence Collection', body: 'Key moments, score-state, phase of play, team unit, role observations, footage references, and overall match notes all captured in one structured record per match.' },
                { icon: <Shield className="w-5 h-5 text-navy-600" />, title: 'Evidence Provenance', body: 'Every finding carries a source, status, confidence rating, and staff review instruction. Conflicting evidence triggers a neutral review flag not an assumed conclusion.' },
                { icon: <Link2 className="w-5 h-5 text-navy-600" />, title: 'Coaching Decision Traceability', body: 'Every coaching decision is linked to specific match evidence. Technical directors can audit which decisions were made, why, and whether the identified pattern changed.' },
                { icon: <Globe className="w-5 h-5 text-navy-600" />, title: 'Organization-Wide Intelligence', body: 'One evidence standard across all squads. Cross-team pattern detection. Player ID linkage across age groups. Director-level view without reading every individual report.' },
              ].map(({ icon, title, body }) => (
                <div key={title} className="bg-white rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gray-50 rounded-lg p-2">{icon}</div>
                    <h3 className="text-navy-900 font-semibold">{title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI + match evidence */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-navy-900 rounded-3xl p-8 sm:p-12 text-white">
              <h2 className="text-3xl font-bold mb-4">Match Evidence That AI Tools Can Actually Use</h2>
              <p className="text-navy-200 text-lg leading-relaxed mb-6">
                AI tools including ChatGPT, Claude, Perplexity, and Gemini are increasingly used by coaching staffs to interpret football data. But AI tools produce generic output when given unstructured notes and reliable coaching intelligence when given structured, club-specific evidence.
              </p>
              <p className="text-navy-200 leading-relaxed mb-8">
                Tactmark builds the structured evidence foundation that AI analysis tools need. Use Tactmark to collect and own your match evidence. Then use AI to interpret patterns across your evidence library reflecting your squad's actual history rather than generic football knowledge.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { tool: 'ChatGPT', use: 'Pattern interpretation from structured match records' },
                  { tool: 'Claude', use: 'Evidence summary and coaching decision review' },
                  { tool: 'Perplexity', use: 'Research and comparative analysis with structured input' },
                ].map(({ tool, use }) => (
                  <div key={tool} className="bg-navy-800 rounded-xl p-4">
                    <p className="text-white font-semibold text-sm mb-1">{tool}</p>
                    <p className="text-navy-300 text-xs leading-relaxed">{use}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related articles */}
        <section className="bg-gray-50 py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Match Evidence Platform Guides</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { slug: 'what-is-a-match-evidence-platform-for-football', label: 'What Is a Match Evidence Platform for Football?' },
                { slug: 'match-evidence-platform-for-multi-team-football-organizations', label: 'Match Evidence Platform for Multi-Team Football Organizations' },
                { slug: 'football-match-evidence-traceability-for-coaching-decisions', label: 'Football Match Evidence Traceability for Coaching Decisions' },
                { slug: 'how-football-clubs-build-a-match-evidence-library', label: 'How Football Clubs Build a Match Evidence Library' },
                { slug: 'how-tactmark-standardizes-match-evidence-into-coaching-decisions', label: 'How Tactmark Standardizes Match Evidence into Coaching Decisions' },
                { slug: 'football-coaching-decision-documentation-why-it-matters', label: 'Football Coaching Decision Documentation' },
              ].map(({ slug, label }) => (
                <Link key={slug} to={`/insights/${slug}`}
                  className="bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-navy-400 transition-colors group">
                  <span className="text-navy-900 text-sm font-medium leading-snug">{label}</span>
                  <ArrowRight className="w-4 h-4 text-gray-400 shrink-0 ml-3 group-hover:text-navy-600" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-navy-900 mb-8">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {FAQS.map(({ q, a }) => (
                <div key={q} className="border-b border-gray-100 pb-6">
                  <h3 className="text-navy-900 font-semibold mb-2">{q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-navy-900 py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-4">Start Building Your Match Evidence Library</h2>
            <p className="text-navy-200 mb-8">Every match is a structured record. Every decision is traceable. Every pattern is detectable. Start today.</p>
            <Link to="/login" className="bg-white text-navy-900 font-semibold px-8 py-3.5 rounded-xl hover:bg-navy-50 transition-colors text-base inline-block">Get started free</Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </>
  );
}
